'use client';

import React, { forwardRef } from 'react';
import { focusRing } from '@utils';
import {
	composeRenderProps,
	Button as RACButton,
	ButtonProps as RACButtonProps,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';

import { LoadingSpinner } from './LoadingSpinner';

interface ButtonProps extends RACButtonProps {
	variant?: 'primary' | 'secondary' | 'tertiary' | 'icon';
	theme?: 'light' | 'dark';
	leftIcon?: React.ReactNode;
	rightIcon?: React.ReactNode;
	isLoading?: boolean;
	loadingText?: string;
}

const button = tv({
	extend: focusRing,
	base: [
		'inline-flex items-center justify-center gap-2',
		'h-12 w-fit min-w-fit cursor-pointer rounded-xs px-4 py-2 text-center text-sm text-nowrap transition sm:px-4 md:px-6',
	],
	variants: {
		variant: {
			primary: '',
			secondary: 'border-3',
			tertiary: '',
			icon: 'text-light-text hover:bg-neutral-lightest size-7 place-content-center rounded-xs !p-1',
		},
		isDisabled: {
			true: 'cursor-not-allowed no-underline',
			false: [
				'hover:decoration-thin hover:underline',
				'focus-visible:decoration-thin underline-offset-2 focus-visible:underline',
				'active:text-light-heading active:bg-neutral-lightest active:border-transparent active:no-underline',
			],
		},
		isLoading: {
			true: 'cursor-progress no-underline hover:no-underline',
		},
	},
	compoundVariants: [
		{
			variant: 'primary',
			theme: 'light',
			class: ['bg-light-action-primary text-light-link-on-action'],
		},
		{
			variant: 'primary',
			theme: 'light',
			isLoading: false,
			isDisabled: false,
			class: 'hover:bg-light-action-primary-hover',
		},
		{
			variant: 'primary',
			theme: 'light',
			isLoading: false,
			isDisabled: true,
			class: 'disabled:bg-neutral-light disabled:text-light-text-lighter',
		},
		{
			variant: 'primary',
			theme: 'dark',
			class: ['bg-dark-action-primary text-dark-link-on-action'],
		},
		{
			variant: 'primary',
			theme: 'dark',
			isLoading: false,
			isDisabled: false,
			class: 'hover:bg-dark-action-primary-hover',
		},
		{
			variant: 'primary',
			theme: 'dark',
			isLoading: false,
			isDisabled: true,
			class: 'disabled:bg-neutral-light-opacity-10 disabled:text-dark-text-lighter',
		},
		{
			variant: 'secondary',
			theme: 'light',
			class: [
				'border-light-action-secondary text-light-link hover:border-light-action-secondary-hover',
			],
		},
		{
			variant: 'secondary',
			theme: 'light',
			isLoading: false,
			isDisabled: false,
			class: 'hover:border-light-action-secondary-hover',
		},
		{
			variant: 'secondary',
			theme: 'light',
			isLoading: false,
			isDisabled: true,
			class: 'disabled:border-neutral-light disabled:text-light-text-lighter',
		},
		{
			variant: 'secondary',
			theme: 'dark',
			class: [
				'border-dark-action-secondary text-dark-link hover:border-dark-action-secondary-hover',
			],
		},
		{
			variant: 'secondary',
			theme: 'dark',
			isLoading: false,
			isDisabled: false,
			class: 'hover:border-dark-action-secondary-hover',
		},
		{
			variant: 'secondary',
			theme: 'dark',
			isLoading: false,
			isDisabled: true,
			class: 'disabled:border-neutral-light-opacity-10 disabled:text-dark-text-lighter',
		},
		{
			variant: 'tertiary',
			theme: 'light',
			class: ['text-light-link bg-transparent'],
		},
		{
			variant: 'tertiary',
			theme: 'light',
			isLoading: false,
			isDisabled: false,
			class: 'hover:bg-neutral-lightest',
		},
		{
			variant: 'tertiary',
			theme: 'light',
			isLoading: false,
			isDisabled: true,
			class: 'disabled:text-light-text-lighter',
		},
		{
			variant: 'tertiary',
			theme: 'dark',
			class: ['text-dark-link bg-transparent'],
		},
		{
			variant: 'tertiary',
			theme: 'dark',
			isLoading: false,
			isDisabled: false,
			class: 'hover:bg-dark-background-shade',
		},
		{
			variant: 'tertiary',
			theme: 'dark',
			isLoading: false,
			isDisabled: true,
			class: 'disabled:text-dark-text-lighter',
		},
	],
	defaultVariants: {
		variant: 'primary',
		theme: 'light',
	},
});

const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
	const {
		variant,
		theme,
		leftIcon,
		rightIcon,
		children,
		isDisabled,
		isLoading,
		loadingText,
		...rest
	} = props;
	return (
		<RACButton
			ref={ref}
			{...rest}
			isDisabled={isDisabled || isLoading}
			className={composeRenderProps(props.className, (className, renderProps) =>
				button({ ...renderProps, variant, isDisabled, isLoading, theme, className }),
			)}
		>
			<>
				{!isLoading && leftIcon}
				{isLoading ? <LoadingSpinner size="small" /> : null}
				{isLoading && loadingText ? loadingText : null}
				{!isLoading && children}
				{!isLoading && rightIcon}
			</>
		</RACButton>
	);
});

Button.displayName = 'Button';

interface CTAButtonProps extends RACButtonProps {
	theme?: 'light' | 'dark';
	size?: 'large' | 'small';
	leftIcon?: React.ReactNode;
	rightIcon?: React.ReactNode;
	animate?: boolean;
}

const callToAction = tv({
	extend: focusRing,
	base: [
		'inline-flex items-center justify-center gap-2 rounded-xs',
		'h-fit w-fit cursor-pointer bg-transparent px-0 py-0 text-nowrap underline decoration-1 underline-offset-2 transition',
		'hover:!decoration-2 focus:!decoration-2 active:bg-transparent active:!decoration-2',
	],
	variants: {
		theme: {
			light: 'text-light-link',
			dark: 'text-dark-link',
		},
		size: {
			large: 'text-md',
			small: 'text-sm',
		},
		isDisabled: {
			true: 'cursor-not-allowed no-underline',
			false: '',
		},
	},
	defaultVariants: {
		size: 'small',
		theme: 'light',
		isDisabled: false,
	},
});

const CTAButton = (props: CTAButtonProps) => {
	const {
		size,
		theme,
		leftIcon,
		rightIcon,
		children,
		isDisabled,
		animate = false,
		...rest
	} = props;

	const leftIconElement =
		leftIcon && animate ? (
			<span className="ml-1 transition-transform group-hover:-translate-x-1.5 group-focus:-translate-x-1.5">
				{leftIcon}
			</span>
		) : (
			leftIcon
		);

	const rightIconElement =
		rightIcon && animate ? (
			<span className="mr-1 transition-transform group-hover:translate-x-1.5 group-focus:translate-x-1.5">
				{rightIcon}
			</span>
		) : (
			rightIcon
		);

	return (
		<RACButton
			{...rest}
			isDisabled={isDisabled}
			className={composeRenderProps(
				props.className,
				(className, renderProps) =>
					callToAction({ ...renderProps, size, theme, isDisabled, className }) + ' group',
			)}
		>
			<>
				{leftIconElement}
				{children}
				{rightIconElement}
			</>
		</RACButton>
	);
};

export type { ButtonProps, CTAButtonProps };
export { Button, CTAButton };
